'use client';

import { useRouter } from 'next/navigation';
import React from 'react';
import { decodeRoomName } from '@/lib/client-utils';

export interface HistoryMeeting {
  roomName: string;
  startedAt: string;
  endedAt?: string;
  status: 'active' | 'ended';
}

export interface AppointmentItem {
  roomName: string;
  startTime: string;
  createdAt: string;
  camOn?: boolean; // 预约是否启用摄像头（true：链接带 ?cam=on，进入者进会前可自行开摄像头）
}

function stripUidPrefix(raw: string): string {
  const decoded = decodeRoomName(raw);
  return decoded.replace(/^u_[^-]+-/, '');
}

// 与首页"预约会议"一致的展示格式：2026/9/8 10:00
function fmtApptTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

// 完整日期时间（含年），用于预约行里看得更清楚
function fmtFullTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

/**
 * 我的会议记录：登录用户可见自己主持过的会议（可重新开启 / 加入 / 删除）
 * 以及预约的会议（可复制链接 / 进入会议 / 取消预约）。
 * 列表可视高度约 3 条，更多时在卡片内上下滑动查看。
 */
export function MeetingHistory() {
  const router = useRouter();
  const [user, setUser] = React.useState<{ id: string; username: string } | null | undefined>(
    undefined,
  );
  const [meetings, setMeetings] = React.useState<HistoryMeeting[] | null>(null);
  const [appointments, setAppointments] = React.useState<AppointmentItem[] | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);
  const [copying, setCopying] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState<string | null>(null);

  // 检测登录态
  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) {
          setUser(d?.ok && d?.user ? d.user : null);
        }
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMeetings = React.useCallback(() => {
    fetch('/api/meetings/history')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.ok && Array.isArray(d.meetings)) {
          setMeetings(d.meetings);
          setAppointments(Array.isArray(d.appointments) ? d.appointments : []);
        }
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!user) {
      setMeetings(null);
      setAppointments(null);
      return;
    }
    loadMeetings();
  }, [user, loadMeetings]);

  // 首页"预约会议"成功后刷新本列表（跨组件通知）
  React.useEffect(() => {
    const onRefresh = () => {
      if (user) loadMeetings();
    };
    window.addEventListener('meeting-history-refresh', onRefresh);
    return () => window.removeEventListener('meeting-history-refresh', onRefresh);
  }, [user, loadMeetings]);

  const openRoom = React.useCallback(
    (roomName: string) => {
      router.push(`/rooms/${encodeURIComponent(roomName)}`);
    },
    [router],
  );

  const restartAndOpen = React.useCallback(
    async (roomName: string) => {
      if (busy) return;
      setBusy(roomName);
      try {
        const res = await fetch('/api/meeting/restart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomName }),
        });
        if (res.ok) {
          openRoom(roomName);
        } else {
          alert('重新开启失败，请重试');
          setBusy(null);
        }
      } catch {
        alert('网络异常，请重试');
        setBusy(null);
      }
    },
    [busy, openRoom],
  );

  const removeRecord = React.useCallback(
    async (m: HistoryMeeting) => {
      if (deleting) return;
      const key = `${m.roomName}|${m.startedAt}`;
      if (!window.confirm(`确定删除会议「${stripUidPrefix(m.roomName)}」的历史记录吗？`)) return;
      setDeleting(key);
      try {
        const res = await fetch('/api/meetings/history/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomName: m.roomName, startedAt: m.startedAt }),
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.deleted) {
          setMeetings((prev) => {
            const next = (prev ?? []).filter(
              (x) => !(x.roomName === m.roomName && x.startedAt === m.startedAt),
            );
            return next;
          });
        } else {
          alert('删除失败，请重试');
        }
      } catch {
        alert('网络异常，请重试');
      } finally {
        setDeleting(null);
      }
    },
    [deleting],
  );

  // 历史场次：复制"会议名 + 链接"分享文案（历史会议无开始时间，与首页未选时间时一致）
  const copyHistory = React.useCallback(
    async (m: HistoryMeeting) => {
      if (copying) return;
      const key = `${m.roomName}|${m.startedAt}`;
      const displayName = stripUidPrefix(m.roomName);
      const url = `${window.location.origin}/rooms/${encodeURIComponent(m.roomName)}`;
      const text = `${displayName} 会议链接：${url}`;
      setCopying(key);
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch {
          setCopying(null);
          return;
        }
      }
      setTimeout(() => setCopying(null), 1500);
    },
    [copying],
  );

  // 预约：进入会议（走完整房名，保留视频标记）
  const openAppointment = React.useCallback(
    (roomName: string, camOn?: boolean) => {
      router.push(`/rooms/${encodeURIComponent(roomName)}${camOn ? '?cam=on' : ''}`);
    },
    [router],
  );

  // 预约：复制完整分享文案（会议名 + 时间 + 链接，与首页"预约会议"一致）
  const copyAppointment = React.useCallback(
    async (a: AppointmentItem) => {
      if (copying) return;
      const displayName = stripUidPrefix(a.roomName);
      const url = `${window.location.origin}/rooms/${encodeURIComponent(a.roomName)}${
        a.camOn ? '?cam=on' : ''
      }`;
      const text = `${displayName} ${fmtApptTime(a.startTime)} 会议链接：${url}`;
      setCopying(a.roomName);
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch {
          setCopying(null);
          return;
        }
      }
      setTimeout(() => setCopying(null), 1500);
    },
    [copying],
  );

  // 预约：取消
  const cancelAppointment = React.useCallback(
    async (a: AppointmentItem) => {
      if (cancelling) return;
      const displayName = stripUidPrefix(a.roomName);
      if (!window.confirm(`确定取消预约「${displayName}」吗？`)) return;
      setCancelling(a.roomName);
      try {
        const res = await fetch('/api/meetings/appointment/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomName: a.roomName }),
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.deleted) {
          setAppointments((prev) => (prev ?? []).filter((x) => x.roomName !== a.roomName));
        } else {
          alert('取消失败，请重试');
        }
      } catch {
        alert('网络异常，请重试');
      } finally {
        setCancelling(null);
      }
    },
    [cancelling],
  );

  if (user === undefined) return null; // 会话探测中
  if (!user) return null; // 未登录不展示

  const fmtTime = (iso?: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const list = meetings ?? [];
  const apptList = appointments ?? [];

  const selKey = (m: HistoryMeeting) => `${m.roomName}|${m.startedAt}`;

  const renderRow = (m: HistoryMeeting) => {
    const displayName = stripUidPrefix(m.roomName);
    const roomPath = decodeRoomName(m.roomName);
    const isBusy = busy === m.roomName;
    const isDeleting = deleting === selKey(m);
    const isCopying = copying === selKey(m);
    return (
      <li
        key={selKey(m)}
        className="rb-mrow"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.6rem',
          padding: '0.6rem 0.8rem',
          background: '#f7f9fe',
          border: '1px solid rgba(31,111,235,0.08)',
          borderRadius: 10,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              color: 'var(--lk-fg, #101828)',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={displayName}
          >
            {displayName}
          </div>
          <div
            style={{
              color: 'var(--lk-fg4, #64748b)',
              fontSize: '0.78rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
            }}
          >
            <span>{fmtTime(m.startedAt)}</span>
            <span
              style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: m.status === 'active' ? '#16a34a' : '#b3bfcf',
              }}
            />
            <span>{m.status === 'active' ? '进行中' : '已结束'}</span>
          </div>
        </div>
        <div
          className="rb-mrow-actions"
          style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexShrink: 0 }}
        >
          <button
            type="button"
            className="lk-button"
            disabled={isDeleting || isCopying}
            style={{
              fontSize: '0.8rem',
              padding: '0.4rem 0.6rem',
              borderRadius: 8,
              background: isCopying ? '#16a34a' : '#ffffff',
              border: isCopying ? 'none' : '1px solid #1f6feb',
              color: isCopying ? '#fff' : '#1f6feb',
              cursor: 'pointer',
            }}
            onClick={() => copyHistory(m)}
          >
            {isCopying ? '已复制 ✓' : '复制链接'}
          </button>
          <button
            type="button"
            className="lk-button"
            disabled={isBusy || isDeleting}
            style={{
              fontSize: '0.85rem',
              padding: '0.4rem 0.85rem',
              borderRadius: 8,
              background: m.status === 'active' ? '#ffffff' : '#1f6feb',
              border: m.status === 'active' ? '1px solid #1f6feb' : 'none',
              color: m.status === 'active' ? '#1f6feb' : '#fff',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (m.status === 'ended') {
                restartAndOpen(roomPath);
              } else {
                openRoom(roomPath);
              }
            }}
          >
            {m.status === 'active' ? '加入会议' : isBusy ? '开启中…' : '重新开启'}
          </button>
          <button
            type="button"
            disabled={isDeleting}
            title="删除这条记录"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.2rem',
              fontSize: '0.8rem',
              padding: '0.4rem 0.55rem',
              borderRadius: 8,
              background: '#fff',
              border: '1px solid rgba(220,38,38,0.35)',
              color: '#dc2626',
              cursor: 'pointer',
              opacity: isDeleting ? 0.5 : 1,
            }}
            onClick={() => removeRecord(m)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            <span>{isDeleting ? '…' : '删除'}</span>
          </button>
        </div>
      </li>
    );
  };

  const renderApptRow = (a: AppointmentItem) => {
    const displayName = stripUidPrefix(a.roomName);
    const isCopying = copying === a.roomName;
    const isCancelling = cancelling === a.roomName;
    return (
      <li
        key={`appt-${a.roomName}`}
        className="rb-mrow"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.6rem',
          padding: '0.6rem 0.8rem',
          background: '#eef5ff',
          border: '1px solid rgba(31,111,235,0.18)',
          borderRadius: 10,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              color: 'var(--lk-fg, #101828)',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
            title={displayName}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
            <span
              style={{
                flexShrink: 0,
                fontSize: '0.68rem',
                color: '#1f6feb',
                background: 'rgba(31,111,235,0.1)',
                border: '1px solid rgba(31,111,235,0.25)',
                borderRadius: 999,
                padding: '0.05rem 0.45rem',
              }}
            >
              已预约
            </span>
          </div>
          <div
            style={{
              color: '#1f6feb',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              marginTop: '0.15rem',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{fmtFullTime(a.startTime)}</span>
          </div>
        </div>
        <div
          className="rb-mrow-actions"
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}
        >
          <button
            type="button"
            className="lk-button"
            disabled={isCopying || isCancelling}
            style={{
              fontSize: '0.8rem',
              padding: '0.38rem 0.6rem',
              borderRadius: 8,
              background: isCopying ? '#16a34a' : '#ffffff',
              border: isCopying ? 'none' : '1px solid #1f6feb',
              color: isCopying ? '#fff' : '#1f6feb',
              cursor: 'pointer',
            }}
            onClick={() => copyAppointment(a)}
          >
            {isCopying ? '已复制 ✓' : '复制链接'}
          </button>
          <button
            type="button"
            className="lk-button"
            disabled={isCopying || isCancelling}
            style={{
              fontSize: '0.8rem',
              padding: '0.38rem 0.7rem',
              borderRadius: 8,
              background: '#1f6feb',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={() => openAppointment(a.roomName, a.camOn)}
          >
            进入会议
          </button>
          <button
            type="button"
            disabled={isCancelling || isCopying}
            title="取消预约"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.15rem',
              fontSize: '0.78rem',
              padding: '0.38rem 0.5rem',
              borderRadius: 8,
              background: '#fff',
              border: '1px solid rgba(220,38,38,0.35)',
              color: '#dc2626',
              cursor: 'pointer',
              opacity: isCancelling ? 0.5 : 1,
            }}
            onClick={() => cancelAppointment(a)}
          >
            {isCancelling ? '…' : '取消'}
          </button>
        </div>
      </li>
    );
  };

  const hasMeetings = meetings !== null && meetings.length > 0;
  const hasAppts = appointments !== null && appointments.length > 0;
  const loading = meetings === null || appointments === null;
  const totalCount = (meetings?.length ?? 0) + (appointments?.length ?? 0);

  return (
    <section
      style={{
        width: '100%',
        maxWidth: 440,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          border: '1px solid rgba(31,111,235,0.12)',
          borderRadius: 18,
          boxShadow: '0 18px 50px rgba(31,111,235,0.12)',
          padding: '1.4rem 1.6rem 1.5rem',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.7rem',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--lk-fg, #101828)' }}>
            我的会议记录
          </h3>
          {!loading && totalCount > 0 && (
            <span style={{ fontSize: '0.78rem', color: 'var(--lk-fg4, #64748b)' }}>
              共 {totalCount} 条
            </span>
          )}
        </div>
        {loading ? (
          <p style={{ margin: '0.4rem 0', color: 'var(--lk-fg4, #64748b)', fontSize: '0.9rem' }}>
            加载中…
          </p>
        ) : totalCount === 0 ? (
          <p style={{ margin: '0.4rem 0', color: 'var(--lk-fg4, #64748b)', fontSize: '0.9rem' }}>
            暂无会议记录。发起会议或预约会议后，这里会显示你的会议。
          </p>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.55rem',
              // 可视高度约 3 条：内容更多时在卡片内上下滑动查看
              maxHeight: '13.4rem',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              paddingRight: '0.15rem',
            }}
          >
            {hasAppts && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    fontSize: '0.8rem',
                    color: '#1f6feb',
                    fontWeight: 600,
                    marginTop: '0.1rem',
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  已预约（{appointments!.length}）
                </div>
                {apptList.map(renderApptRow)}
              </>
            )}
            {hasMeetings && (
              <>
                {hasAppts && (
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--lk-fg4, #64748b)',
                      fontWeight: 600,
                      marginTop: '0.1rem',
                    }}
                  >
                    历史场次（{meetings!.length}）
                  </div>
                )}
                {list.map(renderRow)}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
